/**
 * LlmProvider abstraction contract — E03-F01-T02. Shared by api (the concrete
 * Anthropic / OpenAI providers + the fallback policy) and any consumer that
 * needs to talk about an LLM call without binding to a vendor SDK.
 *
 * Parity source: docs/source-n8n/reference/{AI.md,SR.md,WS.md}. Every n8n
 * "Run LLM" node in the EER app talks to Anthropic (chainLlm / agent / anthropic
 * node) and every matching "Parse & Save" node does ROBUST text extraction over
 * the many shapes the Anthropic node could return:
 *   aiOutput.text || .response || .output
 *   || content[]{ type:'text' }.text  (joined by '\n')
 *   || message.content (string, OR array of text-blocks joined by '\n')
 * Two nodes additionally enable Anthropic web_search:
 *   - SR: search_salary_rate — webSearch=true, NO allowedDomains (whole web),
 *     model claude-haiku-4-5-20251001, retryOnFail=true.
 *   - WS: search_website_for_missing — webSearch=true, allowedDomains=[domain],
 *     max_uses=15, timeout ~120000ms, retryOnFail=true.
 *
 * This file is feature-owned and is re-exported from `@eer/shared` by the serial
 * Wire pass — it must NOT edit the shared barrel during parallel work.
 */
import { z } from 'zod';
/**
 * Canonical default model ids the EER app pins (ai_prompts dump). `model` and
 * `maxTokens` always come from the `ai_prompts` row (T03 acceptance #3) — these
 * are only used as a last-resort fallback when a row is missing.
 */
export const LLM_MODEL_SONNET = 'claude-sonnet-4-5-20250929';
export const LLM_MODEL_HAIKU = 'claude-haiku-4-5-20251001';
/** Default web_search budget for the WS branch (allowedDomains + max_uses=15). */
export const WEB_SEARCH_MAX_USES = 15;
/** web_search timeout in ms (legacy SR/WS nodes: ~120s). */
export const WEB_SEARCH_TIMEOUT_MS = 120_000;
/**
 * web_search options for a single chat call. Mirrors the two legacy variants:
 *   - salary search: `{ enabled:true }`            (no allowedDomains → whole web)
 *   - website search: `{ enabled:true, allowedDomains:[domain], maxUses:15 }`
 */
export const webSearchOptionsSchema = z.object({
    /** Turn on Anthropic web_search for this call. */
    enabled: z.boolean(),
    /** Restrict search to these domains (WS branch = [companyDomain]); empty/absent = whole web (SR branch). */
    allowedDomains: z.array(z.string().min(1)).optional(),
    /** Max number of web_search invocations Claude may make (legacy WS: 15). */
    maxUses: z.number().int().positive().optional(),
});
/**
 * A single chat-completion request. `model` + `maxTokens` are sourced from the
 * `ai_prompts` row (never hard-coded). `prompt` is the fully-substituted user
 * message (the legacy "Build Prompt" output).
 */
export const llmChatRequestSchema = z.object({
    /** Fully-built user prompt (placeholders already substituted). */
    prompt: z.string(),
    /** Model id from ai_prompts.model (e.g. sonnet for most, haiku for salary). */
    model: z.string().min(1),
    /** maxTokens from ai_prompts.max_tokens; provider clamps/ defaults if null. */
    maxTokens: z.number().int().positive().optional(),
    /** Optional system preamble (most EER prompts embed the role in the body). */
    system: z.string().optional(),
    /** web_search options; absent ⇒ plain chat completion. */
    webSearch: webSearchOptionsSchema.optional(),
    /** Per-call timeout in ms (web_search calls use WEB_SEARCH_TIMEOUT_MS). */
    timeoutMs: z.number().int().positive().optional(),
});
//# sourceMappingURL=llm-provider.js.map